import { useEffect, useState } from 'react';
import { X, Phone, User, PhoneCall } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { useI18n } from '../contexts/LanguageContext';
import type { IncomingCall } from '../lib/caller-id';

import { tx } from '../lib/tx';
interface IncomingCallModalProps {
  call: IncomingCall | null;
  customer: {
    id: string;
    name: string;
    phone: string;
    address?: string;
    notes?: string;
  } | null;
  onClose: () => void;
  onViewCustomer: (customerId: string) => void;
  onNewOrder: (customerId: string) => void;
}

export function IncomingCallModal({ call, customer, onClose, onViewCustomer, onNewOrder }: IncomingCallModalProps) {
  const { t, language } = useI18n();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (call) {
      setIsVisible(true);
    }
  }, [call]);

  if (!call) return null;

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const handleViewCustomer = () => {
    if (customer) {
      onViewCustomer(customer.id);
    }
  };

  const handleNewOrder = () => {
    if (customer) {
      onNewOrder(customer.id);
    }
  };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${isVisible ? 'visible' : 'invisible'}`}>
      {/* Backdrop */}
      <div 
        className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className={`relative w-full max-w-md transform transition-all duration-300 ${isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}>
        <Card className="bg-white shadow-2xl overflow-hidden">
          {/* Header - Green gradient for incoming call */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center animate-pulse">
                  <PhoneCall className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">
                    {tx(language, 'Gelen Arama', 'Incoming Call')}
                  </h2>
                  <p className="text-green-100 text-sm">
                    {call.time || new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
              <button 
                onClick={handleClose}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Phone Number Display */}
          <div className="p-6 bg-gray-50 border-b">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm">
                <Phone className="w-5 h-5 text-gray-400" />
                <span className="text-2xl font-bold text-gray-800">{call.phoneNumber}</span>
              </div>
              {call.name && (
                <p className="mt-2 text-gray-600">{call.name}</p>
              )}
            </div>
          </div>

          {/* Customer Info or New Customer Message */}
          <div className="p-6">
            {customer ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{customer.name}</h3>
                    <p className="text-sm text-gray-500">{customer.phone}</p>
                  </div>
                </div>
                
                {customer.address && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">{customer.address}</p>
                  </div>
                )}
                
                {customer.notes && (
                  <div className="mb-4 p-3 bg-yellow-50 rounded-lg border border-yellow-100">
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">📝 </span>
                      {customer.notes}
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button 
                    onClick={handleViewCustomer}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    {tx(language, 'Müşteriyi Görüntüle', 'View Customer')}
                  </Button>
                  <Button 
                    onClick={handleNewOrder}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    {tx(language, 'Yeni Sipariş', 'New Order')}
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-600 mb-4">
                  {language === 'tr' 
                    ? 'Bu numaraya kayıtlı müşteri bulunamadı'
                    : 'No customer found with this phone number'}
                </p>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={handleClose}
                >
                  {tx(language, 'Tamam', 'OK')}
                </Button>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

// Hook to manage incoming call modal state
export function useIncomingCallModal() {
  const [currentCall, setCurrentCall] = useState<IncomingCall | null>(null);
  const [showModal, setShowModal] = useState(false);

  const handleIncomingCall = (call: IncomingCall) => {
    setCurrentCall(call);
    setShowModal(true);
    
    // Auto-hide after 30 seconds if not dismissed
    setTimeout(() => {
      setShowModal(false);
      setCurrentCall(null);
    }, 30000);
  };

  const dismissCall = () => {
    setShowModal(false);
    setTimeout(() => setCurrentCall(null), 300);
  };

  return {
    currentCall,
    showModal,
    handleIncomingCall,
    dismissCall,
  };
}